import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { DatePicker, Tooltip } from "../utils/local-antd";
import type { DatePickerProps } from "../utils/local-antd";
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { createGlobalStyle, useTheme } from "styled-components";

import { Spacing } from "../theme/Spacing";
import { AtiraIcon } from "./AtiraIcon";
import { Flex } from "./Flex";
import { FontSize, Text } from "./Text";
import { LabelDirection, Alignment } from "./shared/types/SharedLabelProps";
import { useAppSelector } from "../redux/store";
import { userSliceSelectors } from "../redux/user/user.selector";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * A string carrying its own instant (`Z`/`+HH:mm` suffix) must be parsed as
 * that instant first and only then *viewed* in `selectedTimezone` -
 * `dayjs.tz(str, zone)` ignores any offset already on `str` and reinterprets
 * its digits as wall-clock time in `zone`, which silently discards the real
 * instant (previously: `dayjs.tz("...T08:00:00.000Z", "Europe/Istanbul")` ->
 * 08:00+03:00 instead of the correct 11:00+03:00). A bare wall-clock string
 * has no instant of its own, so it's the opposite: *interpreted* as
 * `selectedTimezone`, not converted into it.
 */
const getPickerValue = (
  value: number | string | Date | Dayjs,
  selectedTimezone?: string,
) => {
  const hasExplicitOffset =
    typeof value === "string" && /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);

  if (typeof value === "string" && !hasExplicitOffset) {
    return selectedTimezone ? dayjs.tz(value, selectedTimezone) : dayjs(value);
  }

  return selectedTimezone ? dayjs(value).tz(selectedTimezone) : dayjs(value);
};

const ThemedDatePicker = createGlobalStyle`
  .ant-picker-month-btn {
    font-family: inherit;
  }

  .ant-picker-date-panel {
    width: min-content !important;
  }

  .ant-picker-date-panel .ant-picker-content th {
    width: 3rem;
  }

`;

const StyledDatePicker = styled(DatePicker).attrs(
  // @ts-ignore
  (props: AtiraDatePickerProps) => ({
    "data-testid": props.dataTestId,
  }),
)<{ valid?: boolean }>`
  border: 1px solid
    ${({ theme, valid }) => theme[valid ? "lightgray" : "danger"]};
  height: 2.3rem;

  &:focus {
    border: 1px solid ${({ theme }) => theme.lightgray};
    box-shadow: none;
  }

  &:hover {
    border: 1px solid ${({ theme }) => theme.lightgray};
  }
`;

const OptionalSpan = styled.span`
  color: ${({ theme }) => theme.darkerSub};
  font-size: ${FontSize.s};
`;

const InfoSpan = styled.span`
  margin-inline-end: ${Spacing.xs};
  vertical-align: middle;
`;

const StyledLabel = styled.label`
  cursor: pointer;
  color: ${({ theme }) => theme.black};
  width: fit-content;
  flex: 1;
  font-size: ${FontSize.m};
`;

const StyledInfo = styled(Tooltip)`
  cursor: help;
  font-size: ${FontSize.s};
`;

type AtiraDatePickerProps = Omit<
  DatePickerProps,
  "value" | "onChange" | "minDate" | "maxDate"
> & {
  value: number | string | null | Date | Dayjs;
  onChange: (date: Date | string | null) => void;
  titleAlign?: keyof typeof Alignment;
  labelDirection?: keyof typeof LabelDirection;
  info?: string | React.ReactNode;
  valid?: boolean;
  bottomNote?: string | React.ReactNode;
  fromToday?: boolean;
  dataTestId?: string;
  /**
   * A second field's value to bound this one against, e.g. a deadline picker
   * passing its reminder so it can never be set earlier. Takes the same
   * shapes as `value` since callers hold these as the same wall-clock string.
   */
  minDateValue?: number | string | null | Date | Dayjs;
  maxDateValue?: number | string | null | Date | Dayjs;
  /**
   * Overrides the viewer's own account timezone for reading/writing
   * `value` - a booking field must always be read and edited in the
   * *booking's* configured timezone, never whichever timezone the viewing
   * account happens to be set to.
   */
  timezone?: string;
};

export const AtiraDatePicker: React.FC<AtiraDatePickerProps> = ({
  valid = true,
  fromToday,
  minDateValue,
  maxDateValue,
  timezone,
  ...props
}) => {
  const accountTimezone = useAppSelector(userSliceSelectors.selectLoggedInUser)
    ?.settings?.timezone;
  const selectedTimezone = timezone || accountTimezone;

  const [now, setNow] = useState(
    selectedTimezone ? dayjs().tz(selectedTimezone) : dayjs(),
  );

  const theme = useTheme();
  const { t } = useTranslation();
  useEffect(() => {
    const minute = 1000 * 60;

    const getNow = () => {
      return selectedTimezone ? dayjs().tz(selectedTimezone) : dayjs();
    };

    const delay = minute - getNow().second() * 1000 - getNow().millisecond();

    let intervalId: NodeJS.Timeout;

    const timeoutId = setTimeout(() => {
      setNow(getNow());

      intervalId = setInterval(() => {
        setNow(getNow());
      }, minute);
    }, delay);

    return () => {
      clearTimeout(timeoutId);

      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [selectedTimezone]);

  return (
    <Flex
      width={"100%"}
      gap="s"
      flexDirection={
        props.labelDirection === LabelDirection.HORIZONTAL ? "row" : "column"
      }
    >
      {props.title ? (
        <StyledLabel
          htmlFor={props.id}
          style={{
            textAlign: props.titleAlign,
            alignSelf: props.titleAlign || "flex-start",
            margin:
              props.labelDirection === LabelDirection.HORIZONTAL ? "auto" : "0",
          }}
        >
          {props?.info ? (
            <StyledInfo title={props.info}>
              <InfoSpan>
                <AtiraIcon icon={faInfoCircle} color="main" />
              </InfoSpan>
            </StyledInfo>
          ) : null}
          {props.title}
          {props.required ? (
            <span style={{ color: theme.danger }}> *</span>
          ) : (
            <OptionalSpan> ({t("common.optional")})</OptionalSpan>
          )}
        </StyledLabel>
      ) : null}
      <Flex flexDirection="column" gap="xs">
        <StyledDatePicker
          // `fromToday` floors this picker at *now*; a caller-supplied
          // `minDateValue` (e.g. a deadline picker bounding itself against the
          // reminder already chosen) floors it at that instead. When both
          // apply, the later of the two wins - a reminder set for next week
          // still can't push the deadline earlier than today.
          minDate={(() => {
            const floor = fromToday ? now.startOf("minute") : undefined;
            const external = minDateValue
              ? getPickerValue(minDateValue, selectedTimezone)
              : undefined;

            if (floor && external) {
              return floor.isAfter(external) ? floor : external;
            }
            return floor || external;
          })()}
          maxDate={
            maxDateValue
              ? getPickerValue(maxDateValue, selectedTimezone)
              : undefined
          }
          {...(props as any)}
          value={
            props.value ? getPickerValue(props.value, selectedTimezone) : null
          }
          onChange={(date: Dayjs | null) =>
            props.onChange(date ? date.format("YYYY-MM-DDTHH:mm:ss") : null)
          }
          format={props.showTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY"}
          showTime={
            props.showTime
              ? {
                  format: "DD/MM/YYYY:HH:mm",
                  showSecond: false,
                  use12Hours: true,
                }
              : false
          }
          valid={valid}
          placeholder={t("common.select_date")}
        />
        {props.bottomNote ? (
          <Text color="darkerSub" fontSize="xs">
            {props.bottomNote}
          </Text>
        ) : null}
      </Flex>

      <ThemedDatePicker />
    </Flex>
  );
};
